import React, { useState, useRef } from 'react';

function UploadPage() {
  const [bucketName, setBucketName] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!bucketName) {
      setError('Please provide an event or bucket name.');
      return;
    }
    if (files.length === 0) {
      setError('Please select at least one image.');
      return;
    }

    setUploading(true);
    setError('');
    setMessage('');
    setProgress(0);

    // We'll upload in very small chunks (1 file at a time) to give the user a perfectly smooth, real-time progress bar
    // since backend processing (face extraction + S3 upload) takes a few seconds per image.
    const chunkSize = 1;
    let totalUploaded = 0;
    let actualBackendUploaded = 0;
    let actualBackendFaces = 0;

    for (let i = 0; i < files.length; i += chunkSize) {
      const chunk = files.slice(i, i + chunkSize);
      const formData = new FormData();
      formData.append('bucket_name', bucketName.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'));
      
      chunk.forEach(file => {
        formData.append('files', file);
      });

      try {
        const res = await fetch('/api/upload-event-images', {
          method: 'POST',
          body: formData,
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || 'Upload failed');
        }

        totalUploaded += chunk.length;
        
        // Try to parse the numbers from the backend message: "Successfully uploaded X images and indexed Y faces."
        const msg = data.message || "";
        const matchUploads = msg.match(/uploaded (\d+) images/);
        const matchFaces = msg.match(/indexed (\d+) faces/);
        
        if (matchUploads) actualBackendUploaded += parseInt(matchUploads[1], 10);
        if (matchFaces) actualBackendFaces += parseInt(matchFaces[1], 10);

        setProgress(Math.round((totalUploaded / files.length) * 100));
        
      } catch (err) {
        setError(`Error uploading batch: ${err.message}`);
        setUploading(false);
        return;
      }
    }

    setMessage(`Processed ${files.length} files. Saved ${actualBackendUploaded} images with faces to S3, indexing ${actualBackendFaces} total faces.`);
    setUploading(false);
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-[800px] mx-auto w-full">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-extrabold">Upload Event Photos</h2>
      </div>

      <p className="text-textMuted mb-8 leading-relaxed text-lg">
        Select an event name and upload high-resolution photos. 
        The system will automatically generate thumbnails, store originals in cold storage to save costs, 
        and index faces for search.
      </p>

      {message && (
        <div className="bg-[rgba(0,230,118,0.1)] border border-[rgba(0,230,118,0.3)] rounded-xl p-4 mb-6 text-base text-[#00e676]">
          {message}
        </div>
      )}

      {error && (
        <div className="bg-[rgba(255,51,102,0.1)] border border-[rgba(255,51,102,0.3)] rounded-xl p-4 mb-6 text-base text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={handleUpload} className="bg-bg2 p-8 rounded-2xl border border-borderBase">
        <div className="mb-6">
          <label className="block text-sm font-medium text-textMuted mb-2">Event / Bucket Name</label>
          <input
            type="text"
            className="w-full bg-bg3 border border-borderBase rounded-xl px-5 py-3 text-white focus:outline-none focus:border-accentSolid transition-colors"
            placeholder="e.g. smith-wedding-2026"
            value={bucketName}
            onChange={(e) => setBucketName(e.target.value)}
            disabled={uploading}
            required
          />
        </div>

        <div className="mb-8">
          <label className="block text-sm font-medium text-textMuted mb-2">Select Photos</label>
          <div 
            onClick={() => !uploading && fileInputRef.current?.click()}
            className="relative border-2 border-dashed border-borderHover rounded-2xl p-10 text-center hover:bg-bg3 transition-colors cursor-pointer group"
          >
            <input 
              type="file" 
              multiple 
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              ref={fileInputRef}
              disabled={uploading}
              required
            />
            <div className="text-4xl mb-4 group-hover:-translate-y-1 transition-transform">📸</div>
            <p className="text-lg font-medium text-white mb-1">
              {files.length > 0 ? `${files.length} files selected` : "Drag and drop photos here"}
            </p>
            <p className="text-sm text-textMuted">or click to browse from your computer</p>
          </div>
        </div>

        {uploading ? (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-textMuted mb-2">
              <span>Uploading & Processing...</span>
              <span>{progress}%</span>
            </div>
            <div className="bg-bg3 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full bg-accent-gradient rounded-full transition-all duration-300 shadow-glow" 
                style={{ width: `${progress}%` }} 
              />
            </div>
          </div>
        ) : (
          <button
            type="submit"
            className="w-full bg-accent-gradient text-white px-8 py-4 rounded-xl font-bold shadow-glow hover:-translate-y-1 hover:shadow-glow-strong transition-all active:translate-y-px disabled:opacity-50 text-lg"
            disabled={!bucketName || files.length === 0}
          >
            Start Upload
          </button>
        )}
      </form>
    </div>
  );
}

export default UploadPage;
